import React, { useState, useEffect, useMemo } from "react";
import { apiClient } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calculator, Save, Pencil, X } from "lucide-react";

function calcPlayingHandicap(whs, slope, courseRating, par) {
  if (whs === null || whs === undefined || whs === "") return null;
  const raw = parseFloat(whs) * (slope / 113) + (courseRating - par);
  return Math.round(raw);
}

function calcShots(handicaps) {
  // Only calculate if ALL 4 players in the match have handicaps
  if (handicaps.some(h => h === null)) return [null, null, null, null];
  const lowest = Math.min(...handicaps);
  return handicaps.map(h => Math.round(0.9 * (h - lowest)));
}

const emptyMatch = () => ({
  home_player1: { name: "", whs: "" },
  home_player2: { name: "", whs: "" },
  away_player1: { name: "", whs: "" },
  away_player2: { name: "", whs: "" },
});

export default function ShotsCalculator({ fixture, open, onClose }) {
  const [matches, setMatches] = useState([emptyMatch(), emptyMatch(), emptyMatch()]);
  const [courseData, setCourseData] = useState({ slope: 113, courseRating: 72, par: 72 });
  const [homeClubName, setHomeClubName] = useState("");
  const [awayClubName, setAwayClubName] = useState("");
  const [pin, setPin] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && fixture) {
      loadShotsData();
      setPin("");
      setAuthenticated(false);
      setShowPinDialog(false);
    }
  }, [open, fixture?.id]);

  const loadShotsData = async () => {
    if (!fixture) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/fixtures/${fixture.id}/shots`);
      const { home_club, shots_data } = res.data;
      setHomeClubName(fixture.home_club_name);
      setAwayClubName(fixture.away_club_name);
      if (home_club) {
        setCourseData({
          slope: home_club.slope_rating ?? 113,
          courseRating: home_club.course_rating ?? 72,
          par: home_club.par ?? 72,
        });
      }
      if (shots_data) {
        const loaded = [shots_data.match1, shots_data.match2, shots_data.match3].map(m => {
          if (!m || Object.keys(m).length === 0) return emptyMatch();
          return {
            home_player1: { name: m.home_player1?.name || "", whs: m.home_player1?.whs ?? "" },
            home_player2: { name: m.home_player2?.name || "", whs: m.home_player2?.whs ?? "" },
            away_player1: { name: m.away_player1?.name || "", whs: m.away_player1?.whs ?? "" },
            away_player2: { name: m.away_player2?.name || "", whs: m.away_player2?.whs ?? "" },
          };
        });
        setMatches(loaded);
      } else {
        setMatches([emptyMatch(), emptyMatch(), emptyMatch()]);
      }
    } catch (err) {
      console.error("Failed to load shots data", err);
    } finally {
      setLoading(false);
    }
  };

  const updatePlayer = (matchIdx, playerKey, field, value) => {
    setMatches(prev => {
      const updated = [...prev];
      updated[matchIdx] = { ...updated[matchIdx] };
      updated[matchIdx][playerKey] = { ...updated[matchIdx][playerKey], [field]: value };
      return updated;
    });
  };

  const computed = useMemo(() => {
    return matches.map(match => {
      const keys = ["home_player1", "home_player2", "away_player1", "away_player2"];
      const handicaps = keys.map(p =>
        calcPlayingHandicap(match[p].whs, courseData.slope, courseData.courseRating, courseData.par)
      );
      const shots = calcShots(handicaps);
      return { handicaps, shots };
    });
  }, [matches, courseData]);

  const handlePinSubmit = async (pinValue) => {
    const submitPin = pinValue || pin;
    if (submitPin.length !== 7) return;
    let verified = false;
    for (const clubId of [fixture.home_club_id, fixture.away_club_id]) {
      try {
        const r = await apiClient.post(`/clubs/${clubId}/verify-pin`, { pin: submitPin });
        if (r.data.verified) { verified = true; break; }
      } catch (e) {}
    }
    if (!verified) {
      try {
        const r = await apiClient.post('/admin/verify-pin', { pin: submitPin });
        if (r.data.verified) verified = true;
      } catch (e) {}
    }
    if (verified) {
      setAuthenticated(true);
      setPin(submitPin);
      setShowPinDialog(false);
      toast.success("PIN verified — you can now edit");
    } else {
      toast.error("Invalid PIN");
      setPin("");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/fixtures/${fixture.id}/shots?club_pin=${pin}`, {
        match1: matches[0], match2: matches[1], match3: matches[2],
      });
      toast.success("Shots data saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handlePinChange = (e) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
    setPin(raw);
    if (raw.length === 7 && /^[A-Z]{3}[0-9]{4}$/.test(raw)) {
      handlePinSubmit(raw);
    }
  };

  if (!fixture) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-md max-h-[95vh] overflow-y-auto p-0" aria-describedby="shots-desc">
          {/* Compact header */}
          <div className="px-3 pt-3 pb-2 border-b bg-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm" data-testid="shots-title">{homeClubName} vs {awayClubName}</p>
                <p id="shots-desc" className="text-[10px] text-muted-foreground">
                  Slope {courseData.slope} | CR {courseData.courseRating} | Par {courseData.par}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!authenticated ? (
                  <Button size="sm" variant="outline" onClick={() => setShowPinDialog(true)} data-testid="edit-shots-btn">
                    <Pencil size={12} className="mr-1" /> Edit
                  </Button>
                ) : (
                  <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-shots-btn">
                    <Save size={12} className="mr-1" /> {saving ? "..." : "Save"}
                  </Button>
                )}
                <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-shots-btn">
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Match cards - compact for screenshot */}
          <div className="px-3 pb-3 space-y-2">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : (
              matches.map((match, mi) => (
                <MatchCard
                  key={mi}
                  matchIndex={mi}
                  match={match}
                  computed={computed[mi]}
                  homeClubName={homeClubName}
                  awayClubName={awayClubName}
                  onUpdate={updatePlayer}
                  readOnly={!authenticated}
                />
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PIN Entry Dialog */}
      <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
        <DialogContent className="max-w-xs" aria-describedby="pin-desc">
          <DialogHeader>
            <DialogTitle className="text-center text-base">Enter Club PIN</DialogTitle>
            <DialogDescription id="pin-desc" className="text-center text-xs">
              Only involved team captains or admins can edit
            </DialogDescription>
          </DialogHeader>
          <input
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            autoFocus
            maxLength={7}
            value={pin}
            onChange={handlePinChange}
            placeholder="ABC1234"
            className="w-full text-center text-lg font-mono tracking-widest py-3 rounded-lg border-2 outline-none border-border focus:border-primary"
            data-testid="shots-pin-input"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function MatchCard({ matchIndex, match, computed, homeClubName, awayClubName, onUpdate, readOnly }) {
  const players = [
    { key: "home_player1", team: homeClubName, label: "Player 1" },
    { key: "home_player2", team: homeClubName, label: "Player 2" },
    { key: "away_player1", team: awayClubName, label: "Player 1" },
    { key: "away_player2", team: awayClubName, label: "Player 2" },
  ];

  return (
    <div className="bg-gray-50 rounded-lg border p-2 space-y-1" data-testid={`match-card-${matchIndex}`}>
      <p className="text-xs font-semibold text-primary">Match {matchIndex + 1}</p>

      {/* Column headers */}
      <div className="grid grid-cols-[1fr_50px_32px_36px] gap-1 text-[10px] font-medium text-muted-foreground px-0.5">
        <span>Player</span>
        <span className="text-center">WHS</span>
        <span className="text-center">PH</span>
        <span className="text-center">Shots</span>
      </div>

      {players.map((p, pi) => {
        const playerData = match[p.key];
        const ph = computed.handicaps[pi];
        const shots = computed.shots[pi];

        return (
          <div key={p.key} className={`grid grid-cols-[1fr_50px_32px_36px] gap-1 items-center ${pi === 2 ? 'border-t pt-1' : ''}`}>
            <input
              type="text"
              value={playerData.name}
              onChange={(e) => onUpdate(matchIndex, p.key, "name", e.target.value)}
              placeholder={`${p.team} ${p.label}`}
              readOnly={readOnly}
              className={`text-xs px-1.5 py-1 rounded border ${readOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'} border-border outline-none focus:border-primary truncate`}
              data-testid={`${p.key}-name-${matchIndex}`}
            />
            <input
              type="text"
              inputMode="decimal"
              value={playerData.whs}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || v === "-" || /^-?\d*\.?\d*$/.test(v)) {
                  onUpdate(matchIndex, p.key, "whs", v);
                }
              }}
              placeholder="-"
              readOnly={readOnly}
              className={`text-xs text-center px-0.5 py-1 rounded border ${readOnly ? 'bg-gray-100 text-gray-600' : 'bg-white'} border-border outline-none focus:border-primary`}
              data-testid={`${p.key}-whs-${matchIndex}`}
            />
            <span className="text-xs text-center font-medium">{ph !== null ? ph : "-"}</span>
            <span className={`text-xs text-center font-bold ${shots !== null && shots > 0 ? 'text-primary' : ''}`}>
              {shots !== null ? shots : "-"}
            </span>
          </div>
        );
      })}
    </div>
  );
}
